package com.checkin.host.ui.auth

import androidx.compose.runtime.*
import com.checkin.host.api.ApiClient
import com.checkin.host.api.ApiException
import com.checkin.host.ui.components.BrandLogo
import com.checkin.host.theme.AppColors
import com.checkin.host.theme.primaryButton
import com.checkin.host.ui.layout.AlertBanner
import com.checkin.shared.dto.UserDto
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

@Composable
fun VerifyEmailScreen(token: String, onAuthenticated: (UserDto) -> Unit, onBackToLogin: () -> Unit) {
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()

    LaunchedEffect(token) {
        scope.launch {
            try {
                val response = ApiClient.verifyEmail(token)
                ApiClient.token = response.token
                ApiClient.saveUser(response.user)
                clearAuthQueryParams()
                onAuthenticated(response.user)
            } catch (e: ApiException) {
                error = e.message
            } finally {
                loading = false
            }
        }
    }

    AuthCard(title = "Confirmation email", subtitle = "Activation de votre compte") {
        if (loading) {
            P({ style { color(AppColors.muted) } }) { Text("Vérification en cours...") }
        } else if (error != null) {
            AlertBanner(error!!, isError = true)
            Button({ style { primaryButton(); width(100.percent); marginTop(16.px) }; onClick { onBackToLogin() } }) {
                Text("Retour à la connexion")
            }
        }
    }
}

@Composable
fun ResetPasswordScreen(token: String, onDone: () -> Unit) {
    var password by remember { mutableStateOf("") }
    var confirm by remember { mutableStateOf("") }
    var loading by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var success by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()

    AuthCard(title = "Nouveau mot de passe", subtitle = "Choisissez un mot de passe sécurisé") {
        error?.let { AlertBanner(it, isError = true) }
        success?.let { AlertBanner(it, isError = false) }

        if (success == null) {
            com.checkin.host.ui.components.TextField("Nouveau mot de passe", password, org.jetbrains.compose.web.attributes.InputType.Password) {
                password = it
            }
            Div({ style { marginTop(12.px) } }) {
                com.checkin.host.ui.components.TextField("Confirmer", confirm, org.jetbrains.compose.web.attributes.InputType.Password) {
                    confirm = it
                }
            }
            Button({
                style { primaryButton(); width(100.percent); marginTop(20.px) }
                onClick {
                    when {
                        password.length < 8 -> error = "Mot de passe minimum 8 caractères"
                        password != confirm -> error = "Les mots de passe ne correspondent pas"
                        else -> error = null
                    }
                    if (error != null) return@onClick
                    scope.launch {
                        loading = true
                        try {
                            val msg = ApiClient.resetPassword(token, password)
                            clearAuthQueryParams()
                            success = msg.message
                        } catch (e: ApiException) {
                            error = e.message
                        } finally {
                            loading = false
                        }
                    }
                }
            }) { Text(if (loading) "..." else "Enregistrer") }
        } else {
            Button({ style { primaryButton(); width(100.percent) }; onClick { onDone() } }) {
                Text("Se connecter")
            }
        }
    }
}

@Composable
fun VerifyPendingScreen(email: String, onBackToLogin: () -> Unit) {
    var loading by remember { mutableStateOf(false) }
    var message by remember { mutableStateOf<String?>(null) }
    var isError by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()

    AuthCard(
        title = "Confirmez votre email",
        subtitle = "Un lien a été envoyé à $email",
    ) {
        P({ style { color(AppColors.muted); fontSize(14.px); marginBottom(16.px) } }) {
            Text("Cliquez sur le lien dans l'email pour activer votre compte, puis connectez-vous.")
        }
        message?.let { AlertBanner(it, isError) }
        Button({
            style { primaryButton(); width(100.percent); marginBottom(12.px) }
            onClick {
                scope.launch {
                    loading = true
                    try {
                        val response = ApiClient.resendVerification(email)
                        message = response.message
                        isError = false
                    } catch (e: ApiException) {
                        message = e.message
                        isError = true
                    } finally {
                        loading = false
                    }
                }
            }
        }) { Text(if (loading) "..." else "Renvoyer l'email") }
        P({
            style { textAlign("center"); color(AppColors.primary); cursor("pointer"); fontSize(14.px) }
            onClick { onBackToLogin() }
        }) { Text("Retour à la connexion") }
    }
}

@Composable
fun ForgotPasswordScreen(onBackToLogin: () -> Unit) {
    var email by remember { mutableStateOf("") }
    var loading by remember { mutableStateOf(false) }
    var message by remember { mutableStateOf<String?>(null) }
    var isError by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()

    AuthCard(title = "Mot de passe oublié", subtitle = "Nous vous enverrons un lien par email") {
        com.checkin.host.ui.components.TextField("Email", email, org.jetbrains.compose.web.attributes.InputType.Email) {
            email = it
        }
        message?.let { AlertBanner(it, isError) }
        Button({
            style { primaryButton(); width(100.percent); marginTop(20.px) }
            onClick {
                scope.launch {
                    loading = true
                    try {
                        val response = ApiClient.forgotPassword(email.trim())
                        message = response.message
                        isError = false
                    } catch (e: ApiException) {
                        message = e.message
                        isError = true
                    } finally {
                        loading = false
                    }
                }
            }
        }) { Text(if (loading) "..." else "Envoyer le lien") }
        P({
            style { textAlign("center"); marginTop(16.px); color(AppColors.primary); cursor("pointer"); fontSize(14.px) }
            onClick { onBackToLogin() }
        }) { Text("Retour à la connexion") }
    }
}

@Composable
fun AuthCard(title: String, subtitle: String, content: @Composable () -> Unit) {
    Div({
        style {
            property("min-height", "100dvh")
            width(100.percent)
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            backgroundColor(AppColors.sidebar)
            padding(24.px)
        }
    }) {
        Div({
            style {
                width(100.percent)
                property("max-width", "400px")
                backgroundColor(AppColors.surface)
                borderRadius(16.px)
                padding(32.px)
                property("box-shadow", "0 8px 32px rgba(0,0,0,0.15)")
            }
        }) {
            Div({ style { display(DisplayStyle.Flex); justifyContent(JustifyContent.Center); marginBottom(20.px) } }) {
                BrandLogo(size = 48.px, wordmarkSize = 20.px)
            }
            H1({ style { fontSize(24.px); margin(0.px, 0.px, 8.px, 0.px); color(AppColors.text) } }) { Text(title) }
            P({ style { color(AppColors.muted); fontSize(14.px); marginBottom(24.px) } }) { Text(subtitle) }
            content()
        }
    }
}
